// frontend/src/App.tsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import i18n from './i18n/i18n';
import { HelmetProvider } from 'react-helmet-async';
import { GoogleOAuthProvider } from '@react-oauth/google';

// Layout components
import HeaderNew from './components/Layout/HeaderNew';
import FooterNew from './components/Layout/FooterNew';
import SiteChrome from './components/Layout/SiteChrome';

// Page components — lazy loaded for code splitting
import HomePageNew from './pages/HomePageNew';
import NotFoundPage from './pages/NotFoundPage';
import ProtectedRoute from './components/Auth/ProtectedRoute';

const HomePage = React.lazy(() => import('./pages/HomePage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const DepartmentsPage = React.lazy(() => import('./pages/DepartmentsPage'));
const DepartmentDetailPage = React.lazy(() => import('./pages/DepartmentDetailPage'));
const DoctorsPage = React.lazy(() => import('./pages/DoctorsPage'));
const DoctorDetailPage = React.lazy(() => import('./pages/DoctorDetailPage'));
const ServicesPage = React.lazy(() => import('./pages/ServicesPage'));
const ServiceDetailPage = React.lazy(() => import('./pages/ServiceDetailPage'));
const PriceListPage = React.lazy(() => import('./pages/PriceListPage'));
const PriceListPageNew = React.lazy(() => import('./pages/PriceListPageNew'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const BookAppointmentPage = React.lazy(() => import('./pages/BookAppointmentPage'));
const SchedulePage = React.lazy(() => import('./pages/SchedulePage'));
const SearchPage = React.lazy(() => import('./pages/SearchPage'));
const AdminLoginPage = React.lazy(() => import('./pages/AdminLoginPage'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const AdminSchedulePage = React.lazy(() => import('./pages/AdminSchedulePage'));
const AdminStatisticsPage = React.lazy(() => import('./pages/AdminStatisticsPage'));
const ConsultationArchivePage = React.lazy(() => import('./pages/ConsultationArchivePage'));
const ConsultationReviewPage = React.lazy(() => import('./pages/ConsultationReviewPage'));
const AdminQuestionnairePage = React.lazy(() => import('./pages/AdminQuestionnairePage'));
const AdminProtocolTemplatePage = React.lazy(() => import('./pages/AdminProtocolTemplatePage'));
const ProtocolPrintPage = React.lazy(() => import('./pages/ProtocolPrintPage'));
const ScheduleTemplatesPage = React.lazy(() => import('./pages/ScheduleTemplatesPage'));
const AppointmentRequestsPage = React.lazy(() => import('./pages/AppointmentRequestsPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfUsePage = React.lazy(() => import('./pages/TermsOfUsePage'));
const BmiPage = React.lazy(() => import('./pages/BmiPage'));
const HealthToolsPage = React.lazy(() => import('./pages/HealthToolsPage'));
const PreparationsPage = React.lazy(() => import('./pages/PreparationsPage'));
const PreparationDetailPage = React.lazy(() => import('./pages/PreparationDetailPage'));
const IcdPage = React.lazy(() => import('./pages/IcdPage'));
const FaqPage = React.lazy(() => import('./pages/FaqPage'));
const MedicalNewsPage = React.lazy(() => import('./pages/MedicalNewsPage'));
const MedicalNewsDetailPage = React.lazy(() => import('./pages/MedicalNewsDetailPage'));
const CognitiveBiasesPage = React.lazy(() => import('./pages/CognitiveBiasesPage'));
const CognitiveBiasDetailPage = React.lazy(() => import('./pages/CognitiveBiasDetailPage'));
const ClinicSchedulePage = React.lazy(() => import('./pages/ClinicSchedulePage'));

// Личный кабинет пациента
import { PatientAuthProvider } from './contexts/PatientAuthContext';
import PatientRoute from './components/Auth/PatientRoute';

const CabinetLoginPage = React.lazy(() => import('./pages/CabinetLoginPage'));
const CabinetAppointmentsPage = React.lazy(() => import('./pages/CabinetAppointmentsPage'));
const CabinetProfilePage = React.lazy(() => import('./pages/CabinetProfilePage'));
const CabinetDocumentsPage = React.lazy(() => import('./pages/CabinetDocumentsPage'));
const ConsultationRoomPage = React.lazy(() => import('./pages/ConsultationRoomPage'));
const GuestConsultationPage = React.lazy(() => import('./pages/GuestConsultationPage'));

// Providers
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeContextProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';

// UI Components
import FloatingAppointmentButton from './components/UI/FloatingAppointmentButton';
import Cart from './components/Cart/Cart';
import ScrollToTop from './components/ScrollToTop';
import FlowBackground from './components/UI/FlowBackground';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Сервер отдаёт часть данных уже переведёнными (ФИО врача, названия услуг,
// отделений) — по заголовку Accept-Language. Кеш react-query об этом не
// знает и держит ответ, полученный на прежнем языке: до правки страница
// оставалась разноязычной, пока запрос не устареет сам.
//
// Подписка на уровне модуля, а не в компоненте: queryClient живёт столько
// же, сколько приложение, и отписывать её не от чего.
//
// localStorage к этому моменту уже перезаписан — LanguageContext пишет его
// перед changeLanguage, и повторные запросы уйдут с новым языком.
i18n.on('languageChanged', () => {
  queryClient.invalidateQueries();
});

const App: React.FC = () => {
  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID ?? ''}>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeContextProvider>
          <LanguageProvider>
            <AuthProvider>
              <CartProvider>
                <PatientAuthProvider>
                <Router>
                <ScrollToTop />
                <div className="App min-h-screen flex flex-col">
                  <FlowBackground />
                  <HeaderNew />
                  <main className="flex-1">
                    <React.Suspense fallback={<div className="min-h-screen" />}>
                    <Routes>
                      {/* Главная страница */}
                      <Route path="/" element={<HomePageNew />} />
                      <Route path="/old" element={<HomePage />} />

                      {/* О клинике */}
                      <Route path="/about" element={<AboutPage />} />

                      {/* Отделения */}
                      <Route path="/departments" element={<DepartmentsPage />} />
                      <Route path="/departments/:slug" element={<DepartmentDetailPage />} />

                      {/* Врачи */}
                      <Route path="/doctors" element={<DoctorsPage />} />
                      <Route path="/doctors/:slug" element={<DoctorDetailPage />} />

                      {/* Услуги */}
                      <Route path="/services" element={<ServicesPage />} />
                      <Route path="/services/:slug" element={<ServiceDetailPage />} />

                      {/* Прайс-лист */}
                      <Route path="/price-list" element={<PriceListPageNew />} />
                      <Route path="/price-list-old" element={<PriceListPage />} />

                      {/* Поиск */}
                      <Route path="/search" element={<SearchPage />} />

                      {/* Контакты */}
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/clinic-schedule" element={<ClinicSchedulePage />} />
                      <Route path="/privacy" element={<PrivacyPolicyPage />} />
                      <Route path="/terms" element={<TermsOfUsePage />} />

                      {/* Полезное */}
                      <Route path="/useful/health-tools" element={<HealthToolsPage />} />
                      <Route path="/useful/bmi" element={<Navigate to="/useful/health-tools" replace />} />
                      <Route path="/useful/preparations" element={<PreparationsPage />} />
                      <Route path="/useful/preparations/:slug" element={<PreparationDetailPage />} />
                      <Route path="/useful/icd" element={<IcdPage />} />
                      <Route path="/useful/faq" element={<FaqPage />} />
                      <Route path="/useful/medical-news" element={<MedicalNewsPage />} />
                      <Route path="/useful/medical-news/:slug" element={<MedicalNewsDetailPage />} />
                      <Route path="/useful/cognitive-biases" element={<CognitiveBiasesPage />} />
                      <Route path="/useful/cognitive-biases/:slug" element={<CognitiveBiasDetailPage />} />

                      {/* Запись на прием */}
                      <Route path="/book-appointment" element={<BookAppointmentPage />} />

                      {/* Гость по одноразовой ссылке: ни кабинета, ни учётной
                          записи у него нет, поэтому маршрут открытый и живёт
                          вне /cabinet и /admin-panel. */}
                      <Route path="/guest/:inviteToken" element={<GuestConsultationPage />} />

                      {/* Личный кабинет пациента */}
                      <Route path="/cabinet/login" element={<CabinetLoginPage />} />
                      <Route
                        path="/cabinet"
                        element={
                          <PatientRoute>
                            <CabinetAppointmentsPage />
                          </PatientRoute>
                        }
                      />
                      <Route
                        path="/cabinet/profile"
                        element={
                          <PatientRoute>
                            <CabinetProfilePage />
                          </PatientRoute>
                        }
                      />
                      <Route
                        path="/cabinet/documents"
                        element={
                          <PatientRoute>
                            <CabinetDocumentsPage />
                          </PatientRoute>
                        }
                      />
                      <Route
                        path="/cabinet/consultation/:slotId"
                        element={
                          <PatientRoute>
                            <ConsultationRoomPage />
                          </PatientRoute>
                        }
                      />
                      {/* Консультация, заведённая врачом в расписании:
                          у неё нет слота заявки, только бронь. */}
                      <Route
                        path="/cabinet/consultation/booking/:bookingId"
                        element={
                          <PatientRoute>
                            <ConsultationRoomPage />
                          </PatientRoute>
                        }
                      />

                      {/* Админ-панель */}
                      <Route path="/admin-panel/login" element={<AdminLoginPage />} />
                      <Route
                        path="/admin-panel"
                        element={
                          <ProtectedRoute>
                            <AdminDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin-panel/schedule-templates"
                        element={
                          <ProtectedRoute>
                            <ScheduleTemplatesPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin-panel/schedule"
                        element={
                          <ProtectedRoute>
                            <AdminSchedulePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin-panel/appointment-requests"
                        element={
                          <ProtectedRoute>
                            <AppointmentRequestsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin-panel/statistics"
                        element={
                          <ProtectedRoute>
                            <AdminStatisticsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin-panel/consultation-archive"
                        element={
                          <ProtectedRoute>
                            <ConsultationArchivePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin-panel/questionnaires"
                        element={
                          <ProtectedRoute>
                            <AdminQuestionnairePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin-panel/protocol-templates"
                        element={
                          <ProtectedRoute>
                            <AdminProtocolTemplatePage />
                          </ProtectedRoute>
                        }
                      />
                      {/* Печатный лист протокола: открывается в новой
                          вкладке из редактора и с экрана разбора. */}
                      <Route
                        path="/admin-panel/protocol/:id/print"
                        element={
                          <ProtectedRoute>
                            <ProtocolPrintPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin-panel/consultation-review/booking/:bookingId"
                        element={
                          <ProtectedRoute>
                            <ConsultationReviewPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin-panel/consultation-review/:slotId"
                        element={
                          <ProtectedRoute>
                            <ConsultationReviewPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin-panel/consultation/:slotId"
                        element={
                          <ProtectedRoute>
                            <ConsultationRoomPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin-panel/consultation/booking/:bookingId"
                        element={
                          <ProtectedRoute>
                            <ConsultationRoomPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* 404 страница */}
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                    </React.Suspense>
                  </main>
                  {/* В комнате консультации обвязки сайта нет: футер и
                      кнопка записи занимают там больше места, чем сама
                      консультация, особенно на телефоне. */}
                  <SiteChrome>
                    <FooterNew />
                    <FloatingAppointmentButton />
                    <Cart />
                  </SiteChrome>
                </div>
              </Router>
                </PatientAuthProvider>
              </CartProvider>
            </AuthProvider>
          </LanguageProvider>
        </ThemeContextProvider>
      </QueryClientProvider>
    </HelmetProvider>
    </GoogleOAuthProvider>
  );
};

export default App;